import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, Phone, MapPin, ChevronDown } from 'lucide-react';
// Simple SVG icons for social media
const GoogleIcon = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" data-id="element-304">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-305" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-306" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-307" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-308" />
  </svg>;
const FacebookIcon = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" data-id="element-309">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-310" />
  </svg>;
const InstagramIcon = () => <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" data-id="element-311">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-312" />
  </svg>;
const YelpIcon = () => <img src="https://cdn.magicpatterns.com/uploads/8mySe4rPguBxgNyW2CPFuM/yelp2.jpg" alt="Yelp" className="w-3.5 h-3.5 object-contain" data-id="element-313" />;
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'About',
    href: '/about'
  }, {
    name: 'Services',
    href: '/services',
    subLinks: [{
      name: 'Individual Counselling & Therapy',
      href: '/services/individual'
    }, {
      name: 'Couples Therapy',
      href: '/services/couples'
    }, {
      name: 'Family Therapy',
      href: '/services/family'
    }, {
      name: 'Trauma Therapy',
      href: '/services/trauma'
    }, {
      name: 'EMDR Therapy',
      href: '/services/emdr'
    }]
  }, {
    name: 'Fees & Policies',
    href: '/fees'
  }, {
    name: 'Blog',
    href: '/blog'
  }, {
    name: 'Contact',
    href: '/contact'
  }];
  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };
  return <>
      <div className="bg-olive text-white py-2 relative z-50" data-id="element-314">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-315">
          <div className="flex items-center justify-between text-sm font-medium" data-id="element-316">
            <div className="hidden md:flex items-center gap-6" data-id="element-317">
              <a href="mailto:esther@esthermensahcounselling.com" className="flex items-center gap-2 hover:text-gold transition-colors" data-id="element-318">
                <Mail size={14} data-id="element-319" />
                <span data-id="element-320">esther@esthermensahcounselling.com</span>
              </a>
              <a href="tel:5489000692" className="flex items-center gap-2 hover:text-gold transition-colors" data-id="element-321">
                <Phone size={14} data-id="element-322" />
                <span data-id="element-323">548-900-0692</span>
              </a>
              <div className="flex items-center gap-2" data-id="element-324">
                <MapPin size={14} data-id="element-325" />
                <span data-id="element-326">370 Queens Avenue, London, Ontario</span>
              </div>
            </div>
            <div className="flex md:hidden items-center gap-4" data-id="element-327">
              <a href="tel:5489000692" className="flex items-center gap-2 hover:text-gold transition-colors" data-id="element-328">
                <Phone size={14} data-id="element-329" />
                <span data-id="element-330">548-900-0692</span>
              </a>
            </div>
            <div className="flex items-center gap-3" data-id="element-331">
              <a href="https://share.google/icvD2yMNf6yhvy7Im" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors" aria-label="Google Reviews" data-id="element-332">
                <GoogleIcon data-id="element-333" />
              </a>
              <a href="#" className="hover:text-gold transition-colors" aria-label="Facebook" data-id="element-334">
                <FacebookIcon data-id="element-335" />
              </a>
              <a href="#" className="hover:text-gold transition-colors" aria-label="Instagram" data-id="element-336">
                <InstagramIcon data-id="element-337" />
              </a>
              <a href="#" className="hover:text-gold transition-colors" aria-label="Yelp" data-id="element-338">
                <YelpIcon data-id="element-339" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm py-3' : 'bg-white py-5'}`} data-id="element-340">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-olive text-white px-4 py-2 rounded-md z-50" data-id="element-341">
          Skip to main content
        </a>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-342">
          <div className="flex justify-between items-center" data-id="element-343">
            <Link to="/" className="flex items-center gap-3 group" aria-label="Esther Mensah Counselling Home" data-id="element-344">
              <img src="https://cdn.magicpatterns.com/uploads/fz9bznNPehbKxBqTrq2qaw/EstherMensahLogo.NoBG-V3_(1).webp" alt="Esther Mensah Counselling & Psychotherapy" className="h-[60px] md:h-[70px] w-auto" data-id="element-345" />
            </Link>

            <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation" data-id="element-346">
              <ul className="flex items-center gap-5" data-id="element-347">
                {navLinks.map(link => <li key={link.name} className="relative group" data-id="element-348">
                    <Link to={link.href} className={`flex items-center gap-1 font-bold text-sm transition-colors uppercase tracking-wide py-2 ${isActive(link.href) ? 'text-olive' : 'text-charcoal hover:text-olive'}`} data-id="element-349">
                      {link.name}
                      {link.subLinks && <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" data-id="element-350" />}
                    </Link>

                    {/* Desktop Dropdown */}
                    {link.subLinks && <div className="absolute top-full left-0 w-64 bg-white rounded-lg shadow-lg border border-charcoal/5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 overflow-hidden z-50" data-id="element-351">
                        <div className="py-2" data-id="element-352">
                          {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.href} className="block px-4 py-2.5 text-sm font-bold text-charcoal hover:text-olive hover:bg-olive/5 transition-colors" data-id="element-353">
                              {subLink.name}
                            </Link>)}
                        </div>
                      </div>}
                  </li>)}
              </ul>
              <a href="https://esthermensah.janeapp.com/" target="_blank" rel="noopener noreferrer" className="bg-olive hover:bg-olive/90 text-white px-6 py-2.5 rounded-full font-extrabold text-sm transition-colors shadow-md hover:shadow-lg min-h-[44px] flex items-center uppercase tracking-wide" data-id="element-354">
                Book Appointment
              </a>
            </nav>

            <button className="lg:hidden p-2 -mr-2 text-charcoal hover:text-olive transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-expanded={isMobileMenuOpen} aria-label="Toggle navigation menu" data-id="element-355">
              {isMobileMenuOpen ? <X size={24} data-id="element-356" /> : <Menu size={24} data-id="element-357" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden absolute top-full left-0 right-0 bg-white border-t border-cream shadow-lg transition-all duration-300 origin-top ${isMobileMenuOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'}`} data-id="element-358">
          <nav className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-2" aria-label="Mobile navigation" data-id="element-359">
            {navLinks.map(link => <div key={link.name} className="flex flex-col" data-id="element-360">
                <Link to={link.href} className={`font-bold text-lg py-2 border-b border-cream uppercase tracking-wide ${isActive(link.href) ? 'text-olive' : 'text-charcoal hover:text-olive'}`} data-id="element-361">
                  {link.name}
                </Link>
                {/* Mobile Sub-links */}
                {link.subLinks && <div className="flex flex-col pl-4 mt-2 mb-2 space-y-2 border-l-2 border-olive/20 ml-2" data-id="element-362">
                    {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.href} className="font-bold text-base text-charcoal/80 hover:text-olive py-1 transition-colors" data-id="element-363">
                        {subLink.name}
                      </Link>)}
                  </div>}
              </div>)}
            <a href="https://esthermensah.janeapp.com/" target="_blank" rel="noopener noreferrer" className="bg-olive hover:bg-olive/90 text-white px-6 py-3 rounded-full font-extrabold text-center mt-4 transition-colors shadow-md min-h-[44px] uppercase tracking-wide block" data-id="element-364">
              Book Appointment
            </a>
          </nav>
        </div>
      </header>
    </>;
}